<template>
    <div class="container-fluid pt-2">
        <div class="container">
            <div class="row">
                <div class="col-md-6 col-sm-12">
                    <div class="text-left">
                        <h4 class="text-weight-700 text-primary">Expenses</h4>
                    </div>
                </div>
                <div class="col-md-6 col-sm-12">
                    <div class="btn-group float-right">
                        <a v-on:click="CreateModal" class="btn btn-primary btn-sm mx-1 rounded">Create an Expense</a>
                        <a v-on:click="showManageCategories" class="btn btn-outline-secondary btn-sm mx-1 rounded">Manage Categories</a>
                    </div>
                </div>
            </div>

            <div>
                <div class="row py-3">
                    <div class="col-md-6 col-sm-12">
                        <form>
                            <div class="col-auto">
                                <div class="input-group mb-2">
                                    <div class="input-group-prepend">
                                        <div class="input-group-text"><i class="fas fa-search"></i></div>
                                    </div>
                                    <input type="text" class="form-control border border-gray" v-model="searchExpense" placeholder="Search Expenses">
                                </div>
                            </div>
                        </form>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <date-range-picker :auto-apply="autoApply" v-model="dateRange" class="custom-width-datepicker">
                            <template #input="picker" style="width: 100% !important;">
                                <p v-if="picker.startDate !== null && picker.endDate !== null">{{ FormatDate(picker.startDate) }} - {{ FormatDate(picker.endDate) }}</p>
                                <p v-else><i class="bi bi-calendar-event"></i>  Filter by Date</p>
                            </template>
                        </date-range-picker>
                    </div>
                </div>

                <div v-if="loadingExpenses" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading…</span>
                    </div>
                </div>

                <div v-else-if="items.length === 0">
                    <div class="h-100 d-flex justify-content-center align-items-center">
                        <div class="text-center mt-5 pt-5">
                            <h1><i class="bi bi-emoji-smile"></i></h1>
                            <h4 class="text-center py-3">No expenses recorded</h4>
                            <div class="btn-group">
                                <a href="/expenses" class="btn btn-secondary btn-sm rounded mx-1">Reload <i class="bi bi-arrow-repeat"></i></a>
                                <a v-on:click="CreateModal" class="btn btn-primary btn-sm rounded mx-1">Create an Expense</a>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="row">
                    <div class="col-md-6 col-sm-12">
                        <p><i class="fas fa-coins"></i> {{convertCurrencyToLocal(totalexpenses)}}</p>
                        <p><small>All Expenses</small></p>
                    </div>
                    <div class="col-md-6 col-sm-6">
                        <p><i class="bi bi-graph-up" style="font-size: 1em"></i> {{convertCurrencyToLocal(highestexpenses)}}</p>
                        <p><small>Highest Expense</small></p>
                    </div>
                </div>

                <hr v-if="items.length > 0"/>

                <b-table v-if="items.length > 0"
                        sticky-header="100vh"
                        hover
                        :items="items"
                        :fields="fields"
                        sort-icon-left
                        responsive="sm"
                        @row-clicked="RowSelected"
                >
                    <template #cell(index)="data">
                        {{ data.index + 1 }}
                    </template>
                    <template #cell(expense)="data">
                        <p class="text-primary">{{ data.item.expense }}</p>
                    </template>
                </b-table>
            </div>


            <!-- ============================================================ -->
            <!--                          MODALS                               -->
            <!-- ============================================================ -->

            <!-- Create Expense Modal -->
            <b-modal ref="createexpensemodal" id="modal-center" hide-footer static @hidden="resetExpenseForm">
                <div class="text-center">
                    <h2 class="text-weight-700 text-primary">Create Expense</h2>
                    <hr/>
                    <b-form @submit.prevent="CreateExpense">

                        <b-form-input
                                v-model="expenseForm.expense"
                                type="text"
                                class="border my-2"
                                placeholder="Enter Expense Title"
                        ></b-form-input>
                        <div class="text-danger text-left small mb-1" v-if="expenseFormErrors.expense">{{ expenseFormErrors.expense }}</div>

                        <b-form-select class="border my-2" v-model="expenseForm.selectedExpenseCategory" :options="categoryOptions"></b-form-select>
                        <div class="text-danger text-left mb-1" v-if="expenseFormErrors.category">{{ expenseFormErrors.category }}</div>

                        <p class="text-left">Can't see a category? <a @click="createExpenseCategoryModalShow" href="#">Create Expense Category</a></p>

                        <b-form-select class="border my-2" v-model="expenseForm.selectedAccount" :options="accountSelectOptions"></b-form-select>
                        <p class="text-left small text-muted mb-2">Optional — leave as Auto to post to the account matching this category.</p>

                        <!-- Quick-add category inline form -->
                        <div v-if="showQuickAddCategory" class="border rounded p-2 mb-2 text-left bg-light">
                            <p class="mb-1"><strong>New Category</strong></p>
                            <div class="d-flex">
                                <b-form-input v-model="quickCategoryName" type="text" class="border mr-1" placeholder="Category name" size="sm" @keyup.enter="submitQuickCategory" ref="quickCategoryInput"></b-form-input>
                                <b-button size="sm" variant="primary" class="mr-1 " @click="submitQuickCategory" :disabled="savingCategory || !quickCategoryName.trim()">
                                    <span v-if="savingCategory"><i class="bi bi-hourglass-split"></i></span>
                                    <span v-else><i class="bi bi-check-lg"></i></span>
                                </b-button>
                                <b-button size="sm" variant="outline-secondary" @click="showQuickAddCategory = false; quickCategoryName = ''">
                                    <i class="bi bi-x-lg"></i>
                                </b-button>
                            </div>
                            <small class="text-danger" v-if="quickCategoryError">{{ quickCategoryError }}</small>
                        </div>

                        <date-range-picker
                                v-model="expenseForm.expenseDate"
                                class="w-100"
                                single-date-picker
                                :ranges="false"
                                auto-apply
                        >
                            <template #input="picker" style="width: 100% !important;">
                                <p v-if="picker.startDate">{{ FormatDate(picker.startDate) }}</p>
                                <p v-else><i class="bi bi-calendar-event"></i>  Select to change expense date</p>
                            </template>
                        </date-range-picker>

                        <hr/>

                        <b-textarea
                                v-model="expenseForm.notes"
                                class="border my-2"
                                placeholder="Notes/Reason"
                        ></b-textarea>

                        <b-form-input
                                v-model="expenseForm.expenseAmount"
                                type="number"
                                step="0.01"
                                min="0"
                                class="border my-2"
                                placeholder="Amount Spent"
                        ></b-form-input>
                        <div class="text-danger text-left small mb-1" v-if="expenseFormErrors.amount">{{ expenseFormErrors.amount }}</div>

                        <b-button type="submit" variant="primary" class="btn-block mt-2" :disabled="savingExpense || showApprovePrompt">
                            {{ savingExpense ? 'Saving…' : 'Submit' }}
                        </b-button>

                        <!-- Approve prompt shown after successful creation (admin/manager only) -->
                        <div v-if="showApprovePrompt" class="mt-3 border rounded p-3 bg-light text-center">
                            <p class="mb-2 font-weight-bold">Expense created! Would you like to approve it now?</p>
                            <div class="btn-group">
                                <button type="button" class="btn btn-primary btn-sm rounded mr-2" @click="proceedToApprove">Yes, Approve Now</button>
                                <button type="button" class="btn btn-outline-secondary btn-sm rounded" @click="skipApprove">No, Do It Later</button>
                            </div>
                        </div>

                    </b-form>
                </div>
            </b-modal>

            <!-- Create Expense Category Modal -->
            <b-modal ref="createExpenseCategoryModal" id="modal-cat-create" hide-footer static @hidden="quickCategoryName = ''; quickCategoryError = ''">
                <p class="my-4 text-center text-primary">Create Expense Category</p>
                <b-form @submit.prevent="createExpenseCategory">

                    <b-form-input
                            v-model="expensecategoryname"
                            type="text"
                            class="border my-2"
                            placeholder="Enter Expense Category"
                            required
                    ></b-form-input>

                    <b-form-input
                            v-model="expensecategorydesc"
                            type="text"
                            class="border my-2"
                            placeholder="Description (optional)"
                    ></b-form-input>

                    <small class="text-danger" v-if="categoryCreateError">{{ categoryCreateError }}</small>

                    <b-button type="submit" variant="primary" class="btn-block" :disabled="savingCategory">
                        {{ savingCategory ? 'Saving…' : 'Submit' }}
                    </b-button>

                </b-form>
            </b-modal>

            <!-- Edit Expense Modal -->
            <b-modal ref="editexpensemodal" id="modal-edit-expense" hide-footer static @hidden="resetExpenseForm">
                <div class="text-center">
                    <h2 class="text-weight-700 text-primary text-capitalize">Edit {{selectedexpense.expense}}</h2>
                    <hr/>
                    <b-form @submit.prevent="editExpenseSub">

                        <b-form-input
                                v-model="expenseForm.expense"
                                type="text"
                                class="border my-2"
                                placeholder="Enter Expense Title"
                        ></b-form-input>

                        <b-form-select class="border my-2" v-model="expenseForm.selectedExpenseCategory" :options="categoryOptions"></b-form-select>

                        <b-form-select class="border my-2" v-model="expenseForm.selectedAccount" :options="accountSelectOptions"></b-form-select>
                        <p class="text-left small text-muted mb-2">Optional — leave as Auto to post to the account matching this category.</p>

                        <b-form-input
                                v-model="expenseForm.expenseAmount"
                                type="number"
                                step="0.01"
                                min="0"
                                class="border my-2"
                                placeholder="Amount Spent"
                        ></b-form-input>

                        <date-range-picker
                                v-model="expenseForm.expenseDate"
                                class="w-100"
                                single-date-picker
                                :ranges="false"
                                auto-apply
                        >
                            <template #input="picker" style="width: 100% !important;">
                                <p v-if="picker.startDate">{{ FormatDate(picker.startDate) }}</p>
                                <p v-else><i class="bi bi-calendar-event"></i>  Select to change expense date</p>
                            </template>
                        </date-range-picker>

                        <b-button type="submit" variant="primary" class="btn-block mt-2" :disabled="savingExpense">
                            {{ savingExpense ? 'Saving…' : 'Submit' }}
                        </b-button>

                    </b-form>
                </div>
            </b-modal>

            <!-- Expense Details Modal -->
            <b-modal ref="expenseShowDetailsModal" id="bv-modal-approve" hide-footer static>
                <div class="d-block text-left">
                    <h5 class="text-weight-800 py-1">Expense Details</h5>
                    <div class="pb-2">
                        <h5 class="text-weight-400 py-1">Expense: <span>{{selectedexpense.expense}}</span></h5>
                        <h5 class="text-weight-400 py-1">Amount: <span>{{convertCurrencyToLocal(selectedexpense.amount)}}</span></h5>
                        <h5 class="text-weight-400 py-1" v-if="selectedexpense.reason">Reason/notes: {{ selectedexpense.reason }}</h5>
                        <h5 class="text-weight-400 py-1">Created by: <span class="text-capitalize">{{selectedexpense.created_by}}</span></h5>
                        <h5 class="text-weight-400 py-1">Status: <span>{{selectedexpense.status}}</span></h5>
                        <h5 class="text-weight-400 py-1">Created on: <span>{{selectedexpense.created_on}}</span></h5>
                        <h5 class="text-weight-400 py-1" v-if="selectedexpense.expense_scope">
                            Period:
                            <span class="badge badge-light">
                                {{ selectedexpense.expense_scope === 'one_off' ? 'One-off' : selectedexpense.expense_scope === 'monthly' ? 'Monthly' : 'Annual' }}
                            </span>
                        </h5>
                        <h5 class="text-weight-400 py-1" v-if="selectedexpense.funding_source">
                            Covered by:
                            <span v-if="selectedexpense.funding_source === 'business_cash'" class="badge badge-secondary">Business Cash / Sales</span>
                            <span v-else-if="selectedexpense.funding_source === 'payment_method'" class="badge badge-primary">Business Payment Method</span>
                            <span v-else class="badge badge-info">External — {{ selectedexpense.external_source_label || 'External Source' }}</span>
                        </h5>
                        <h5 class="text-weight-400 py-1">
                            Deducts from sales:
                            <span :class="selectedexpense.deduct_from_sales ? 'badge badge-warning' : 'badge badge-success'">
                                {{ selectedexpense.deduct_from_sales ? 'Yes' : 'No' }}
                            </span>
                        </h5>
                    </div>
                    <hr/>
                    <p class="text-danger">To change the expense date just click edit and change the date</p>
                    <hr/>
                    <div v-if="selectedexpense.status === 'Pending'">
                        <div class="btn-group">
                            <button v-if="canManageExpenses" class="btn btn-outline-danger rounded btn-sm mx-1"
                                v-on:click="actionExpense('delete', selectedexpense.expense_id)">Delete</button>
                            <button v-if="canApproveExpenses"
                                class="btn btn-outline-dark btn-sm rounded mx-1"
                                v-on:click="actionExpense('reject', selectedexpense.expense_id)">Reject</button>
                            <button class="btn btn-outline-dark rounded btn-sm mx-1"
                                v-on:click="editExpense(selectedexpense.expense_id)">Edit</button>
                            <button v-if="canApproveExpenses"
                                class="btn btn-primary btn-sm rounded mx-1"
                                v-on:click="approveExpenseModal()">Approve</button>
                        </div>
                    </div>
                    <div v-else>
                        <div class="btn-group">
                            <button class="btn btn-outline-danger rounded btn-sm mx-1"
                                v-on:click="actionExpense('delete', selectedexpense.expense_id)">Delete</button>
                            <button class="btn btn-outline-dark rounded btn-sm mx-1"
                                v-on:click="editExpense(selectedexpense.expense_id)">Edit</button>
                        </div>
                    </div>
                </div>
            </b-modal>

            <!-- Approve Expense Modal (admin / can manage expenses only) -->
            <b-modal ref="expenseModalApprove" hide-footer static title="Approve Expense">
                <div class="d-block text-left">
                    <div class="text-center mb-3">
                        <h5 class="mb-0">{{ selectedexpense.expense }}</h5>
                        <h4 class="text-primary font-weight-bold">{{ convertCurrencyToLocal(selectedexpense.amount) }}</h4>
                        <small class="text-muted" v-if="selectedexpense.reason">{{ selectedexpense.reason }}</small>
                    </div>

                    <hr/>

                    <!-- Expense Period -->
                    <div class="form-group">
                        <label class="font-weight-bold">Expense Period</label>
                        <select class="form-control" v-model="approvalForm.expense_scope">
                            <option value="one_off">One-off (today)</option>
                            <option value="monthly">Monthly (e.g. rent, salaries)</option>
                            <option value="annual">Annual</option>
                        </select>
                        <small class="text-muted" v-if="approvalForm.expense_scope !== 'one_off'">
                            Monthly/annual expenses will <strong>not</strong> reduce today's sales figure.
                        </small>
                    </div>

                    <!-- Funding Source -->
                    <div class="form-group">
                        <label class="font-weight-bold">Covered by</label>
                        <div class="border rounded p-2">
                            <div class="custom-control custom-radio mb-2">
                                <input type="radio" id="apr-fs-pm" class="custom-control-input" value="payment_method" v-model="approvalForm.funding_source">
                                <label class="custom-control-label" for="apr-fs-pm">
                                    Business Payment Method
                                    <small class="text-muted d-block">Cash, M-Pesa, Bank, Card, etc.</small>
                                </label>
                            </div>
                            <div class="custom-control custom-radio">
                                <input type="radio" id="apr-fs-ext" class="custom-control-input" value="external" v-model="approvalForm.funding_source">
                                <label class="custom-control-label" for="apr-fs-ext">
                                    External Source
                                    <small class="text-muted d-block">Director's account, investor, loan — business cash not affected</small>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Payment method picker (business payment method) -->
                    <div class="form-group" v-if="approvalForm.funding_source === 'payment_method'">
                        <label class="font-weight-bold">Payment Method Used</label>
                        <small class="text-danger d-block" v-if="noPaymentSelected">{{ noPaymentSelected }}</small>
                        <select class="form-control" v-model="sourceOfExpenseFunds">
                            <option :value="null">Select payment method</option>
                            <option v-for="payment in approveExpensePayments" :key="payment.id" :value="payment.id">
                                {{ payment.payment_method }}
                            </option>
                        </select>
                    </div>

                    <!-- External source label -->
                    <div class="form-group" v-if="approvalForm.funding_source === 'external'">
                        <label class="font-weight-bold">Source Name</label>
                        <input type="text" class="form-control"
                            v-model="approvalForm.external_source_label"
                            placeholder="e.g. Director's Account, KCB Loan, Investor"
                        />
                        <small class="text-muted">This expense will not reduce business sales or cash.</small>
                    </div>

                    <div class="my-2">
                        <button
                            class="btn btn-primary btn-block rounded"
                            :disabled="savingApproval"
                            v-on:click="approveExpense(selectedexpense.expense_id)">
                            {{ savingApproval ? 'Approving…' : 'Approve Expense' }}
                        </button>
                    </div>
                </div>
            </b-modal>

            <!-- Manage Categories Modal -->
            <b-modal ref="manageCategoriesModal" id="modal-manage-cats" hide-footer size="lg" title="Manage Expense Categories">

                <!-- Add new category inline -->
                <div class="mb-3">
                    <p class="text-primary"><strong>Add New Category</strong></p>
                    <b-form @submit.prevent="submitCategoryForm" class="d-flex flex-wrap">
                        <b-form-input v-model="categoryForm.name" type="text" class="border mr-2 mb-2" placeholder="Category name" style="max-width:220px" required></b-form-input>
                        <b-form-input v-model="categoryForm.description" type="text" class="border mr-2 mb-2" placeholder="Description (optional)" style="max-width:220px"></b-form-input>
                        <b-button type="submit" variant="primary" class="mb-2 rounded" :disabled="savingCategory">
                            {{ savingCategory && !editingCategory ? 'Saving…' : 'Add' }}
                        </b-button>
                    </b-form>
                    <small class="text-danger" v-if="categoryFormErrors.name">{{ categoryFormErrors.name }}</small>
                </div>

                <hr/>

                <!-- Category loading -->
                <div v-if="loadingCategories" class="text-center py-3">
                    <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
                    <span class="ml-2 text-muted">Loading…</span>
                </div>

                <!-- Category empty state -->
                <div v-else-if="categories.length === 0" class="text-center py-3 text-muted">
                    <i class="bi bi-tags" style="font-size:2rem"></i>
                    <p class="mt-2">No categories yet. Add one above.</p>
                </div>

                <!-- Category list -->
                <div v-else>
                    <div v-for="cat in categories" :key="cat.id" class="d-flex align-items-center py-2 border-bottom">

                        <!-- View mode -->
                        <div v-if="editingCategory !== cat.id" class="flex-grow-1">
                            <strong>{{ cat.name }}</strong>
                            <span class="text-muted ml-2" v-if="cat.description && cat.description !== cat.name">— {{ cat.description }}</span>
                            <span class="badge badge-light ml-2">{{ cat.expenses_count || 0 }} expenses</span>
                        </div>

                        <!-- Inline edit mode -->
                        <div v-else class="flex-grow-1 d-flex flex-wrap">
                            <b-form-input v-model="categoryEditForm.name" type="text" class="border mr-2 mb-1" placeholder="Name" style="max-width:180px" size="sm"></b-form-input>
                            <b-form-input v-model="categoryEditForm.description" type="text" class="border mr-2 mb-1" placeholder="Description" style="max-width:180px" size="sm"></b-form-input>
                            <b-button size="sm" variant="success" class="mr-1 mb-1 rounded" @click="submitEditCategory(cat)" :disabled="savingCategory">
                                {{ savingCategory ? '…' : 'Save' }}
                            </b-button>
                            <b-button size="sm" variant="outline-secondary" class="mb-1 rounded" @click="editingCategory = null">Cancel</b-button>
                        </div>

                        <!-- Actions -->
                        <div v-if="editingCategory !== cat.id" class="ml-2 flex-shrink-0">
                            <b-button size="sm" variant="outline-secondary" class="mr-1 rounded" @click="startEditCategory(cat)">
                                <i class="bi bi-pencil"></i>
                            </b-button>
                            <b-button size="sm" variant="outline-danger" class="rounded" @click="confirmDeleteCategory(cat)">
                                <i class="bi bi-trash"></i>
                            </b-button>
                        </div>
                    </div>
                </div>
            </b-modal>

            <!-- Delete Category Confirmation Modal -->
            <b-modal ref="deleteCategoryModal" id="modal-delete-cat" hide-footer centered>
                <div class="text-center py-2">
                    <i class="bi bi-exclamation-triangle text-warning" style="font-size:2rem"></i>
                    <h5 class="mt-3">Delete "{{ categoryToDelete ? categoryToDelete.name : '' }}"?</h5>
                    <p class="text-muted">Categories with linked expenses cannot be deleted.</p>
                </div>
                <div class="d-flex justify-content-center pb-2">
                    <b-button variant="outline-secondary" class="mr-2 rounded" @click="$refs.deleteCategoryModal.hide()">Cancel</b-button>
                    <b-button variant="danger" class="rounded" @click="submitDeleteCategory" :disabled="deletingCategory">
                        {{ deletingCategory ? 'Deleting…' : 'Yes, Delete' }}
                    </b-button>
                </div>
            </b-modal>

        </div>
    </div>
</template>

<script>
    import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
    import { mapGetters } from 'vuex';

    export default {
        name: 'expenses',
        components: { DateRangePicker },

        data() {
            return {
                // Expense list
                expenses: '',
                highestexpenses: '',
                totalexpenses: '',
                items: [],
                loadingExpenses: false,
                fields: [
                    'index',
                    { key: 'expense', sortable: true },
                    { key: 'amount',  sortable: true },
                    { key: 'status',  sortable: true },
                    { key: 'created_by', sortable: true },
                    { key: 'created_on', sortable: true },
                ],

                // Selected expense (details / edit)
                selectedexpense: {},
                expenseId: '',

                // Filters
                searchExpense: '',
                dateRange: { startDate: null, endDate: null },
                autoApply: true,

                // Expense form (employee fields only)
                expenseForm: {
                    expense: '',
                    selectedExpenseCategory: null,
                    selectedAccount: null,
                    expenseAmount: '',
                    notes: '',
                    expenseDate: { startDate: null, endDate: null },
                },
                expenseFormErrors: {},
                savingExpense: false,
                showApprovePrompt: false,
                paymentMethodOptions: [],

                // Category dropdown data (reactive source of truth)
                categories: [],
                loadingCategories: false,

                // Chart of accounts, for the optional account picker
                chartOfAccounts: [],

                // Quick-add category (inside create-expense modal)
                showQuickAddCategory: false,
                quickCategoryName: '',
                quickCategoryError: '',
                savingCategory: false,

                // Stand-alone create category modal
                expensecategoryname: '',
                expensecategorydesc: '',
                categoryCreateError: '',

                // Approve expense
                approveExpensePayments: [],
                sourceOfExpenseFunds: null,
                noPaymentSelected: '',
                savingApproval: false,
                approvalForm: {
                    funding_source: 'payment_method',
                    external_source_label: '',
                    expense_scope: 'one_off',
                    payment_type_id: null,
                },

                // Category management (Manage Categories modal)
                categoryForm: { name: '', description: '' },
                categoryFormErrors: {},
                categoryEditForm: { name: '', description: '' },
                editingCategory: null,
                categoryToDelete: null,
                deletingCategory: false,
            };
        },

        computed: {
            isAdminOrOwner() {
                return ((this.userDetails && this.userDetails.roles) || []).some(r => r.name === 'Admin' || r.name === 'owner');
            },
            canManageExpenses() {
                return this.isAdminOrOwner || this.hasPermission('can manage expenses');
            },
            // Approve / reject (the server allows approve-expenses for both).
            canApproveExpenses() {
                return this.canManageExpenses || this.hasPermission('approve-expenses');
            },
            ...mapGetters(['userDetails']),

            // Reactive options list for b-form-select — auto-updates whenever categories changes
            categoryOptions() {
                return [
                    { value: null, text: 'Select Category' },
                    ...this.categories.map(c => ({ value: c.id, text: c.name })),
                ];
            },

            // Grouped b-form-select options (rendered as optgroups) for the
            // chart-of-accounts picker — auto-updates whenever chartOfAccounts changes.
            accountSelectOptions() {
                return [
                    { value: null, text: 'Auto (based on category)' },
                    ...this.chartOfAccounts
                        .filter(g => (g.accounts || []).length > 0)
                        .map(g => ({
                            label: g.name,
                            options: g.accounts.map(a => ({ value: a.id, text: a.name })),
                        })),
                ];
            },
        },

        mounted() {
            this.checkIfExpenseIdHasBeenSet();
            this.loadCategories();
            this.loadAccounts();
            this.loadPaymentMethods();
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('openCreate')) {
                this.CreateModal();
            }
        },

        watch: {
            searchExpense() {
                this.getExpenses();
            },
            dateRange: {
                deep: true,
                handler(val) {
                    if (val.startDate && val.endDate) {
                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.set('start-date', val.startDate.toISOString().split('T')[0]);
                        urlParams.set('end-date',   val.endDate.toISOString().split('T')[0]);
                        window.history.replaceState(null, null, '?' + urlParams.toString());
                    }
                    this.getExpenses();
                },
            },
        },

        methods: {
            // ─── Helpers ──────────────────────────────────────────────────────────

            FormatDate(date) {
                return moment(date).format('DD-MMM-YYYY');
            },

            hasPermission(permissionName) {
                return this.userDetails.permissions &&
                    (this.userDetails?.permissions || []).some(p => p.name === permissionName);
            },

            resetExpenseForm() {
                this.expenseForm = {
                    expense: '',
                    selectedExpenseCategory: null,
                    selectedAccount: null,
                    expenseAmount: '',
                    notes: '',
                    expenseDate: { startDate: null, endDate: null },
                };
                this.expenseFormErrors = {};
                this.showApprovePrompt = false;
                this.showQuickAddCategory = false;
                this.quickCategoryName = '';
                this.quickCategoryError = '';
            },

            validateExpenseForm() {
                const errors = {};
                if (!this.expenseForm.expense.trim())         errors.expense  = 'Expense title is required.';
                if (!this.expenseForm.selectedExpenseCategory) errors.category = 'Please select a category.';
                if (!this.expenseForm.expenseAmount || parseFloat(this.expenseForm.expenseAmount) < 0)
                    errors.amount = 'A valid amount is required.';
                this.expenseFormErrors = errors;
                return Object.keys(errors).length === 0;
            },

            // ─── Expense list ─────────────────────────────────────────────────────

            checkIfExpenseIdHasBeenSet() {
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.has('expense-id')) {
                    this.expenseId = urlParams.get('expense-id');
                }
                this.getExpenses();
            },

            async getExpenses() {
                this.loadingExpenses = true;
                const urlParams = new URLSearchParams(window.location.search);
                try {
                    const { data } = await axios.get('/api/expenses', {
                        params: {
                            search:    this.searchExpense,
                            startdate: urlParams.get('start-date'),
                            enddate:   urlParams.get('end-date'),
                            expenseId: this.expenseId,
                        },
                    });

                    this.totalexpenses   = data.totalexpenses;
                    this.highestexpenses = data.highestexpenses;

                    if (this.expenseId === '') {
                        this.items = (data.expenses || []).map(v => this.normaliseExpense(v));
                    } else {
                        const singleItem = this.normaliseExpense(data.expenses);
                        this.items = [singleItem];
                        this.selectedexpense = singleItem;
                        this.$refs.expenseShowDetailsModal.show();
                    }
                } catch (e) {
                    // silent
                } finally {
                    this.loadingExpenses = false;
                }
            },

            RowSelected(item) {
                this.selectedexpense = item;
                this.$refs.expenseShowDetailsModal.show();
            },

            // ─── Categories ───────────────────────────────────────────────────────

            async loadCategories() {
                this.loadingCategories = true;
                try {
                    const { data } = await axios.get('/api/expense-categories');
                    this.categories = data.categories;
                } catch (e) {
                    // silent
                } finally {
                    this.loadingCategories = false;
                }
            },

            // ─── Chart of accounts (optional picker) ───────────────────────────────

            async loadAccounts() {
                try {
                    const { data } = await axios.get('/api/accounts-for-selection');
                    this.chartOfAccounts = Array.isArray(data) ? data : [];
                } catch (e) {
                    // silent — picker just stays on "Auto"
                }
            },

            // ─── Create expense ───────────────────────────────────────────────────

            CreateModal() {
                this.resetExpenseForm();
                this.$refs.createexpensemodal.show();
            },

            async CreateExpense() {
                if (!this.validateExpenseForm()) return;
                this.savingExpense = true;
                try {
                    const { data } = await axios.post('/api/expense/create', {
                        expensetitle:  this.expenseForm.expense,
                        category_id:   this.expenseForm.selectedExpenseCategory,
                        account_id:    this.expenseForm.selectedAccount,
                        expenseamount: this.expenseForm.expenseAmount,
                        notes:         this.expenseForm.notes,
                        expenseDate:   this.expenseForm.expenseDate,
                    });

                    if (data.status === 200) {
                        this.getExpenses();
                        const canApprove = this.canApproveExpenses;
                        if (canApprove) {
                            // Seed selectedexpense from the response so approve modal is ready
                            this.selectedexpense = this.normaliseExpense(data.expense);
                            this.showApprovePrompt = true;
                        } else {
                            this.$refs.createexpensemodal.hide();
                            this.$toasts.push({ type: 'success', message: data.message });
                        }
                    } else {
                        this.$toasts.push({ type: 'error', message: data.message });
                    }
                } catch (e) {
                    const msg = e.response && e.response.data && e.response.data.message
                        ? e.response.data.message : 'Something went wrong.';
                    this.$toasts.push({ type: 'error', message: msg });
                } finally {
                    this.savingExpense = false;
                }
            },

            // ─── Quick-add category (inside create expense modal) ─────────────────

            createExpenseCategoryModalShow() {
                this.showQuickAddCategory = true;
                this.quickCategoryName = '';
                this.quickCategoryError = '';
                this.$nextTick(() => {
                    if (this.$refs.quickCategoryInput) this.$refs.quickCategoryInput.focus();
                });
            },

            async submitQuickCategory() {
                if (!this.quickCategoryName.trim()) {
                    this.quickCategoryError = 'Category name is required.';
                    return;
                }
                this.savingCategory = true;
                this.quickCategoryError = '';
                try {
                    const { data } = await axios.post('/api/expense-category', {
                        category: this.quickCategoryName.trim(),
                    });
                    // Push to the reactive array → categoryOptions computed auto-updates
                    this.categories.push(data.category);
                    // Auto-select the newly created category
                    this.expenseForm.selectedExpenseCategory = data.category.id;
                    this.showQuickAddCategory = false;
                    this.quickCategoryName = '';
                    this.$toasts.push({ type: 'success', message: 'Category created and selected.' });
                } catch (e) {
                    const msg = e.response && e.response.data && e.response.data.message
                        ? e.response.data.message : 'Failed to create category.';
                    this.quickCategoryError = msg;
                } finally {
                    this.savingCategory = false;
                }
            },

            // ─── Stand-alone create category modal ───────────────────────────────

            async createExpenseCategory() {
                if (!this.expensecategoryname.trim()) {
                    this.categoryCreateError = 'Category name is required.';
                    return;
                }
                this.savingCategory = true;
                this.categoryCreateError = '';
                try {
                    const { data } = await axios.post('/api/expense-category', {
                        category:    this.expensecategoryname.trim(),
                        description: this.expensecategorydesc.trim(),
                    });
                    this.categories.push(data.category);
                    this.expenseForm.selectedExpenseCategory = data.category.id;
                    this.$refs.createExpenseCategoryModal.hide();
                    this.expensecategoryname = '';
                    this.expensecategorydesc = '';
                    this.$toasts.push({ type: 'success', message: 'Category created and selected.' });
                } catch (e) {
                    const msg = e.response && e.response.data && e.response.data.message
                        ? e.response.data.message : 'Failed to create category.';
                    this.categoryCreateError = msg;
                } finally {
                    this.savingCategory = false;
                }
            },

            // ─── Edit expense ─────────────────────────────────────────────────────

            editExpense(expense_id) {
                const expense = this.items.find(i => i.expense_id === expense_id);
                if (expense) {
                    this.expenseForm.expense                 = expense.expense;
                    this.expenseForm.selectedExpenseCategory = expense.category_id || null;
                    this.expenseForm.selectedAccount         = expense.account_id || null;
                    this.expenseForm.expenseAmount           = expense.amount;
                    this.expenseForm.expenseDate             = { startDate: null, endDate: null };
                }
                this.$refs.expenseShowDetailsModal.hide();
                this.$refs.editexpensemodal.show();
            },

            async editExpenseSub() {
                this.savingExpense = true;
                try {
                    const { data } = await axios.put('/api/expense', {
                        expense_id:              this.selectedexpense.expense_id,
                        expense:                 this.expenseForm.expense,
                        selectedExpenseCategory: this.expenseForm.selectedExpenseCategory,
                        account_id:              this.expenseForm.selectedAccount,
                        expenseAmount:           this.expenseForm.expenseAmount,
                        expenseDate:             this.expenseForm.expenseDate,
                    });

                    if (data.status === 200) {
                        this.$refs.editexpensemodal.hide();
                        this.$toasts.push({ type: 'success', message: data.message });
                        this.getExpenses();
                    } else {
                        this.$toasts.push({ type: 'error', message: data.message });
                    }
                } catch (e) {
                    this.$toasts.push({ type: 'error', message: 'Failed to update expense.' });
                } finally {
                    this.savingExpense = false;
                }
            },

            // ─── Expense actions (delete, reject) ─────────────────────────────────

            actionExpense(action, id) {
                axios.post('/api/expenseaction/' + action, { expense_id: id })
                    .then(({ data }) => {
                        this.$refs.expenseShowDetailsModal.hide();
                        this.$toasts.push({ type: data.type, message: data.message });
                        this.getExpenses();
                    })
                    .catch(() => {});
            },

            // ─── Approve expense ──────────────────────────────────────────────────

            proceedToApprove() {
                this.$refs.createexpensemodal.hide();
                this.approveExpenseModal();
            },

            skipApprove() {
                this.$refs.createexpensemodal.hide();
                this.$toasts.push({ type: 'success', message: 'Expense created' });
            },

            approveExpenseModal() {
                this.sourceOfExpenseFunds = null;
                this.noPaymentSelected = '';
                // Seed from any previously saved values on this expense
                this.approvalForm = {
                    funding_source:        this.selectedexpense.funding_source        || 'payment_method',
                    external_source_label: this.selectedexpense.external_source_label || '',
                    expense_scope:         this.selectedexpense.expense_scope         || 'one_off',
                    payment_type_id:       this.selectedexpense.payment_type_id       || null,
                };
                this.getPaymentDetails();
                this.$refs.expenseShowDetailsModal.hide();
                this.$refs.expenseModalApprove.show();
            },

            normaliseExpense(v) {
                let status = '';
                if (v.status === 1)      status = 'Approved';
                else if (v.status === 0) status = 'Pending';
                else if (v.status === 2) status = 'Rejected';
                return {
                    expense_id:            v.id,
                    expense:               v.expense,
                    reason:                v.reason,
                    amount:                v.amount,
                    status,
                    category_id:           v.expense_category ? v.expense_category.id : null,
                    account_id:            v.account_id || null,
                    created_by:            v.created_by ? v.created_by.name : '',
                    created_on:            moment(v.created_at).format('HH:mm DD-MM-YYYY'),
                    funding_source:        v.funding_source        || 'business_cash',
                    external_source_label: v.external_source_label || null,
                    expense_scope:         v.expense_scope         || 'one_off',
                    deduct_from_sales:     !!v.deduct_from_sales,
                    payment_type_id:       v.payment_id            || null,
                };
            },

            async loadPaymentMethods() {
                try {
                    const { data } = await axios.get('/api/payments');
                    this.paymentMethodOptions = (Array.isArray(data) ? data : []).filter(p => p.enabled);
                } catch (e) { /* silent */ }
            },

            getPaymentDetails() {
                axios.get('/api/payments', { params: { enabled: true } })
                    .then(({ data }) => {
                        this.approveExpensePayments = Array.isArray(data) ? data.filter(p => p.enabled) : [];
                    })
                    .catch(() => {});
            },

            async approveExpense(id) {
                const isExternal = this.approvalForm.funding_source === 'external';
                if (!isExternal && !this.sourceOfExpenseFunds) {
                    this.noPaymentSelected = 'You need to select the payment method used';
                    return;
                }
                this.savingApproval = true;
                try {
                    await axios.post('/api/expense-approve', {
                        expenseId:             id,
                        expenseSource:         this.sourceOfExpenseFunds || null,
                        funding_source:        this.approvalForm.funding_source,
                        external_source_label: this.approvalForm.external_source_label || null,
                        expense_scope:         this.approvalForm.expense_scope,
                        payment_type_id:       this.approvalForm.payment_type_id || null,
                    });
                    this.$refs.expenseModalApprove.hide();
                    this.$toasts.push({ type: 'success', message: 'Expense approved' });
                    this.getExpenses();
                } catch (e) {
                    this.$toasts.push({ type: 'error', message: 'Failed to approve expense.' });
                } finally {
                    this.savingApproval = false;
                }
            },

            // ─── Category management (Manage Categories modal) ────────────────────

            showManageCategories() {
                this.categoryForm = { name: '', description: '' };
                this.categoryFormErrors = {};
                this.editingCategory = null;
                this.$refs.manageCategoriesModal.show();
            },

            async submitCategoryForm() {
                if (!this.categoryForm.name.trim()) {
                    this.categoryFormErrors = { name: 'Category name is required.' };
                    return;
                }
                this.savingCategory = true;
                this.categoryFormErrors = {};
                try {
                    const { data } = await axios.post('/api/expense-category', {
                        category:    this.categoryForm.name.trim(),
                        description: this.categoryForm.description.trim(),
                    });
                    this.categories.push(data.category);
                    this.categoryForm = { name: '', description: '' };
                    this.$toasts.push({ type: 'success', message: data.message });
                } catch (e) {
                    const msg = e.response && e.response.data && e.response.data.message
                        ? e.response.data.message : 'Failed to create category.';
                    this.categoryFormErrors = { name: msg };
                } finally {
                    this.savingCategory = false;
                }
            },

            startEditCategory(cat) {
                this.editingCategory = cat.id;
                this.categoryEditForm = { name: cat.name, description: cat.description || '' };
            },

            async submitEditCategory(cat) {
                if (!this.categoryEditForm.name.trim()) return;
                this.savingCategory = true;
                try {
                    const { data } = await axios.put('/api/expense-category', {
                        id:          cat.id,
                        name:        this.categoryEditForm.name.trim(),
                        description: this.categoryEditForm.description.trim(),
                    });
                    const idx = this.categories.findIndex(c => c.id === cat.id);
                    if (idx !== -1) this.$set(this.categories, idx, data.category);
                    this.editingCategory = null;
                    this.$toasts.push({ type: 'success', message: data.message });
                } catch (e) {
                    this.$toasts.push({ type: 'error', message: 'Failed to update category.' });
                } finally {
                    this.savingCategory = false;
                }
            },

            confirmDeleteCategory(cat) {
                this.categoryToDelete = cat;
                this.$refs.deleteCategoryModal.show();
            },

            async submitDeleteCategory() {
                if (!this.categoryToDelete) return;
                this.deletingCategory = true;
                try {
                    await axios.delete('/api/expense-category/' + this.categoryToDelete.id);
                    this.categories = this.categories.filter(c => c.id !== this.categoryToDelete.id);
                    this.$refs.deleteCategoryModal.hide();
                    this.$toasts.push({ type: 'success', message: 'Category deleted.' });
                    this.categoryToDelete = null;
                } catch (e) {
                    const msg = e.response && e.response.data && e.response.data.message
                        ? e.response.data.message : 'Failed to delete category.';
                    this.$toasts.push({ type: 'error', message: msg });
                } finally {
                    this.deletingCategory = false;
                }
            },
        },
    };
</script>

<style>
    .custom-width-datepicker {
        width: 100% !important;
    }
</style>
